import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { locales, localeLabels, type Locale } from '@sonasi/shared-types';

import { MarkdownField } from '@/components/MarkdownField';
import { TranslateWithAi } from '@/components/TranslateWithAi';
import { saveMessage, type LegalKind } from '@/lib/apps';
import {
  documentFor,
  useAppsUsingStudioLegal,
  useSaveStudioLegal,
  useStudioLegal,
} from '@/lib/studio-legal';

/**
 * The studio's terms and privacy policy — the text every app falls back to.
 *
 * One document per kind and language, edited one at a time. Not a single form
 * over all ten: they are ten separate legal documents, and a Save covering the
 * lot would make "unsaved changes" true about a document nobody opened.
 */

/*
 * Short labels, because the design draws these as one segmented control rather
 * than as two headings. "Terms of Service" and "Privacy Policy" are what the
 * documents are called; "Terms" and "Privacy" are what the switch between them
 * is called, and the title field below carries the full name anyway.
 */
const KINDS: { value: LegalKind; label: string }[] = [
  { value: 'TERMS', label: 'Terms' },
  { value: 'PRIVACY', label: 'Privacy' },
];

interface Draft {
  title: string;
  effectiveOn: string;
  body: string;
}

const BLANK: Draft = { title: '', effectiveOn: '', body: '' };

export default function LegalPage() {
  const documents = useStudioLegal();
  const save = useSaveStudioLegal();

  const [kind, setKind] = useState<LegalKind>('TERMS');
  const [locale, setLocale] = useState<Locale>('en');

  const stored = documentFor(documents.data, kind, locale);
  const saved = useMemo<Draft>(
    () =>
      stored
        ? { title: stored.title, effectiveOn: stored.effectiveOn, body: stored.body }
        : /*
           * A language nobody has written starts empty rather than prefilled
           * with the English text. Prefilling and pressing Save would store the
           * English words as the Japanese translation, and the tab would then
           * report a document that does not exist.
           */
          BLANK,
    [stored],
  );

  const [draft, setDraft] = useState<Draft>(saved);

  /*
   * Keyed on kind and locale, so switching tabs loads that document rather
   * than leaving the previous one's text under a new heading. On a refetch
   * of the same tab, only an untouched box follows the row: a translation
   * applied from the modal while somebody was typing 日本語 on this very tab
   * would otherwise replace their typing with the model's, unasked. What
   * they typed stays; Save then writes it over the translation, which is
   * what the box on screen says will happen.
   */
  const touched = useRef(false);
  const shown = useRef({ kind, locale });
  useEffect(() => {
    const switched = shown.current.kind !== kind || shown.current.locale !== locale;
    shown.current = { kind, locale };
    if (switched || !touched.current) {
      setDraft(saved);
      touched.current = false;
    }
  }, [saved, kind, locale]);

  const audience = useAppsUsingStudioLegal(kind, locale);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const complete = draft.title.trim() !== '' && draft.effectiveOn !== '' && draft.body.trim() !== '';

  /*
   * Saved from a button on the page rather than the header's save bar, which
   * the design keeps for the app and post editors. `save.mutate` and not
   * `save`: `useMutation` returns a fresh object every render while `mutate` is
   * stable.
   */
  const mutate = save.mutate;
  const commit = useCallback(() => {
    if (!complete) return;
    mutate(
      {
        kind,
        locale,
        document: {
          title: draft.title.trim(),
          effectiveOn: draft.effectiveOn,
          body: draft.body.trim(),
        },
      },
      // Saved is no longer "typed": the row may follow a refetch again.
      { onSuccess: () => (touched.current = false) },
    );
  }, [complete, draft, kind, locale, mutate]);

  const edit = (patch: Partial<Draft>) => {
    touched.current = true;
    setDraft((current) => ({ ...current, ...patch }));
  };

  /*
   * The design's second button. A translator starts from the English wording
   * far more often than from nothing, and typing it out again is how the two
   * drift apart. Offered only where it means something: not on English itself,
   * and not when there is no English document to copy.
   */
  const english = documentFor(documents.data, kind, 'en');
  const canCopyEnglish = locale !== 'en' && Boolean(english);
  const copyEnglish = () => {
    if (!english) return;
    edit({ title: english.title, effectiveOn: english.effectiveOn, body: english.body });
  };

  const sections = draft.body.split('\n').filter((line) => line.startsWith('## ')).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Studio documents</h2>
          <span className="ml-auto text-[11px]" style={{ color: 'var(--dim)' }}>
            Used by every app that has not written its own
          </span>
        </span>

        {/*
          Said where the saving happens, because that is when it matters.
          Overwriting with no history is a decision, not an oversight, and a
          legal document is exactly the kind of text a reader assumes is kept.
        */}
        <span
          className="flex items-start gap-2.5 rounded-lg px-[11px] py-[9px]"
          style={{
            background: 'color-mix(in oklab, var(--warn) 10%, transparent)',
            border: '1px solid color-mix(in oklab, var(--warn) 35%, transparent)',
          }}
        >
          <span
            aria-hidden="true"
            className="mt-[5px] h-[5px] w-[5px] flex-none rounded-full"
            style={{ background: 'var(--warn)' }}
          />
          <span className="text-[11.5px] leading-[1.5]" style={{ color: 'var(--muted)' }}>
            Saving replaces the text — there is no version history. Set the effective date to say
            when this wording starts applying.
          </span>
        </span>

        <span
          role="tablist"
          aria-label="Document"
          className="inline-flex self-start rounded-lg border p-0.5"
          style={{ background: 'var(--panel2)', borderColor: 'var(--line)' }}
        >
          {KINDS.map((one) => (
            <button
              key={one.value}
              type="button"
              role="tab"
              aria-selected={kind === one.value}
              onClick={() => setKind(one.value)}
              className="seg focus-ring min-h-[26px] cursor-pointer px-2.5 py-[5px] text-[11.5px]"
            >
              {one.label}
            </button>
          ))}
        </span>

        <span className="flex flex-wrap items-center gap-1.5">
          <span className="flex flex-wrap gap-1.5" role="tablist" aria-label="Language">
            {locales.map((one) => {
            const written = Boolean(documentFor(documents.data, kind, one));
            return (
              <button
                key={one}
                type="button"
                role="tab"
                aria-selected={locale === one}
                onClick={() => setLocale(one)}
                className="chip focus-ring cursor-pointer"
              >
                <span>{localeLabels[one]}</span>
                {/*
                  Which languages exist is the first thing somebody needs here,
                  and it is not a percentage: a legal document is written or it
                  is not. "not written" is the state that sends every app's
                  terms page in that language to the English text.
                */}
                <span
                  className="font-mono text-[10px] tracking-normal"
                  style={{ color: written ? 'var(--ok)' : 'var(--dim)' }}
                >
                  {written ? 'written' : 'not written'}
                </span>
              </button>
            );
          })}
          </span>

          {/*
            The same button the app and post editors have, translating
            *this* document — `terms` or `privacy` is its id. Offered on the
            English tab only, like theirs, and only while the English on
            screen is the English that is saved: the run reads the row, not
            the box, and a translation of wording somebody is still changing
            is a translation of the wrong wording. Mounted whatever the tab,
            so switching to watch a language land does not end the run.
          */}
          <span className="ml-auto flex items-center gap-2.5">
            {locale === 'en' && dirty ? (
              <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
                Save the English document before translating it.
              </span>
            ) : null}
            <TranslateWithAi
              kind="legal"
              id={kind.toLowerCase()}
              offered={locale === 'en' && !dirty && Boolean(stored)}
            />
          </span>
        </span>
      </div>

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        {/*
          The design's two columns: the title takes what is left, the date takes
          150px. It was a wrapping flex row, which put the date on its own line
          on any panel narrower than about 420px and left the title short of the
          panel edge on every wider one.
        */}
        <div className="grid gap-2.5 [grid-template-columns:minmax(0,1fr)_150px]">
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="field-label">Title</span>
            <input
              key={`title-${kind}-${locale}`}
              value={draft.title}
              onChange={(event) => edit({ title: event.target.value })}
              className="field w-full"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="field-label">Effective from</span>
            <input
              key={`date-${kind}-${locale}`}
              type="date"
              value={draft.effectiveOn}
              onChange={(event) => edit({ effectiveOn: event.target.value })}
              className="field field-mono w-full"
            />
          </label>
        </div>

        {/*
          `key` on the field, so switching document or language remounts it
          rather than carrying the previous one's scroll position and preview
          state into a document it does not belong to.
        */}
        <MarkdownField
          key={`body-${kind}-${locale}`}
          label="Document · one heading per section"
          value={draft.body}
          onChange={(body) => edit({ body })}
          placeholder="## 1. About these terms"
          meta={`${sections} sections · ${draft.body.length} chars`}
          rows={14}
          mono
        />

        <span className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            disabled={!dirty || !complete || save.isPending}
            onClick={commit}
            className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save document'}
          </button>
          {canCopyEnglish ? (
            <button
              type="button"
              onClick={copyEnglish}
              className="btn-small focus-ring cursor-pointer"
            >
              Copy English into this language
            </button>
          ) : null}
          {dirty && !complete ? (
            <span className="text-[11.5px]" style={{ color: 'var(--muted)' }}>
              A document needs a title, an effective date and a body before it can be saved.
            </span>
          ) : null}
          {save.isError ? (
            <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
              {saveMessage(save.error)}
            </span>
          ) : null}
        </span>
      </div>

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Apps showing this</h2>
          <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            {audience.data?.length ?? 0} apps
          </span>
        </span>
        {/*
          So that "does changing this affect anyone" is a list rather than
          something to remember. An app is here only when it has no document of
          its own for this language *and* none in English — its own English copy
          is what it falls back to first.
        */}
        <div data-rows>
          {(audience.data ?? []).map((app) => (
            <div
              key={app.id}
              className="flex items-center gap-2.5 border-b py-2"
              style={{ borderColor: 'var(--line)' }}
            >
              <span className="flex-1 text-[12.5px]">{app.name}</span>
              <span className="font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
                /{app.slug}
              </span>
            </div>
          ))}
          {audience.data?.length === 0 ? (
            <p className="m-0 py-2 text-[12.5px]" style={{ color: 'var(--muted)' }}>
              None — every app has written its own for this language.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
